import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpOutlineIcon,
  Category as CategoryIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Settings as SettingsIcon,
  ViewCarouselOutlined as ViewCarouselOutlinedIcon,
  PersonOutline as PersonOutlineIcon
} from '@mui/icons-material';
import { 
  getFAQSettings, 
  saveFAQSettings, 
  saveFAQCategory, 
  deleteFAQCategory, 
  saveFAQItem, 
  deleteFAQItem 
} from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import FaqSectionsPanel from './FaqSectionsPanel';

interface FAQCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  enabled: boolean;
  order: number;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  categoryId: string;
  enabled: boolean;
  helpful: number;
  notHelpful: number;
  order: number;
}

interface FAQSettings {
  enabled: boolean;
  categories: FAQCategory[];
  faqs: FAQ[];
}

interface FAQSettingsComponentProps {
  companyId: string | null;
  configId: string;
}

const defaultFAQSettings: FAQSettings = {
  enabled: true,
  categories: [
    {
      id: 'orders',
      name: 'Order & Purchase',
      icon: '🛒',
      description: 'Order placement and payment processes',
      enabled: true,
      order: 0
    },
    {
      id: 'shipping',
      name: 'Shipping & Delivery',
      icon: '🚚',
      description: 'Shipping process and delivery details',
      enabled: true,
      order: 1
    },
    {
      id: 'returns',
      name: 'Return & Cancellation',
      icon: '↩️',
      description: 'Return conditions and cancellation process',
      enabled: true,
      order: 2
    },
    {
      id: 'account',
      name: 'Account & Membership',
      icon: '👤',
      description: 'Account settings and membership operations',
      enabled: true,
      order: 3
    },
    {
      id: 'payment',
      name: 'Payment & Invoice',
      icon: '💳',
      description: 'Payment methods and billing',
      enabled: true,
      order: 4
    }
  ],
  faqs: [
    {
      id: '1',
      question: 'How can I track my order?',
      answer: 'To track your order, sign in to your account and go to the "My Orders" section. You can find your tracking number there and view detailed tracking on the shipping company website.',
      categoryId: 'orders',
      enabled: true,
      helpful: 45,
      notHelpful: 3,
      order: 0
    },
    {
      id: '2',
      question: 'How do I return a product?',
      answer: 'You can return your product within 14 days from the delivery date. You can request a return from the "My Account > Returns and Cancellations" section. The product must be in original packaging and resellable condition.',
      categoryId: 'returns',
      enabled: true,
      helpful: 38,
      notHelpful: 2,
      order: 0
    },
    {
      id: '3',
      question: 'How much is the shipping fee?',
      answer: 'Shipping is free for orders above 150 TL. For orders below 150 TL, the shipping fee is 29.90 TL. An additional 15 TL fee applies for express delivery.',
      categoryId: 'shipping',
      enabled: true,
      helpful: 52,
      notHelpful: 5,
      order: 0
    },
    {
      id: '4',
      question: 'Which payment methods do you accept?',
      answer: 'We accept credit cards (Visa, Mastercard, American Express), debit cards, bank transfer/EFT, and cash on delivery. We also offer installment payment options.',
      categoryId: 'payment',
      enabled: true,
      helpful: 31,
      notHelpful: 1,
      order: 0
    },
    {
      id: '5',
      question: 'I forgot my password, how can I reset it?',
      answer: 'Click the "Forgot Password" link on the login page. Enter your email address and reset your password using the link sent to you. The link is valid for 24 hours.',
      categoryId: 'account',
      enabled: true,
      helpful: 28,
      notHelpful: 2,
      order: 0
    },
    {
      id: '6',
      question: 'How long does delivery take?',
      answer: 'Standard delivery takes 2-5 business days, and express delivery takes 1-2 business days. Same-day delivery is also available in major cities. Delivery times may vary by product and region.',
      categoryId: 'shipping',
      enabled: true,
      helpful: 41,
      notHelpful: 4,
      order: 1
    },
    {
      id: '7',
      question: 'How can I use a discount coupon?',
      answer: 'Enter your coupon code in the "Discount Coupon" field on the cart page and click "Apply". If valid, the discount amount will be deducted from your cart automatically.',
      categoryId: 'orders',
      enabled: true,
      helpful: 35,
      notHelpful: 3,
      order: 1
    },
    {
      id: '8',
      question: 'Can I exchange a product?',
      answer: 'Yes, you can exchange a product within 14 days. First, start a return process; after the return is completed, you can place a new order. No additional shipping fee is charged for exchange.',
      categoryId: 'returns',
      enabled: true,
      helpful: 29,
      notHelpful: 2,
      order: 1
    }
  ]
};

const FAQSettingsComponent: React.FC<FAQSettingsComponentProps> = ({ companyId, configId }) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<FAQSettings>(defaultFAQSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [faqConfigId, setFaqConfigId] = useState<string | null>(null);
  
  // Modal states
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FAQCategory | null>(null);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  
  // Delete confirmation modals
  const [deleteCategoryModalOpen, setDeleteCategoryModalOpen] = useState(false);
  const [deleteFaqModalOpen, setDeleteFaqModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [faqToDelete, setFaqToDelete] = useState<string | null>(null);
  
  // Modal validation errors
  const [categoryModalError, setCategoryModalError] = useState<string>('');
  const [faqModalError, setFaqModalError] = useState<string>('');

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: '',
    description: ''
  });
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    categoryId: ''
  });

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // 0: Sayfa SSS Blokları · 1: Hesabım SSS
  const [mainTab, setMainTab] = useState(0);

  // Sık kullanılan emojiler
  const commonEmojis = [
    '🛒', '📦', '🚚', '💳', '💰', '🎁', '⭐', '❤️', '👍', '✅',
    '❌', '⚠️', '📱', '💻', '🖥️', '⌚', '📷', '🎮', '👕', '👗',
    '👞', '🎒', '📚', '✏️', '🔧', '🔨', '⚙️', '🔑', '🏠', '🏢',
    '🏪', '🏬', '🛍️', '🎯', '📊', '📈', '💡', '🔔', '📧', '📞',
    '💬', '💭', '🔍', '🔎', '📝', '📋', '📌', '📍', '🗂️', '📁',
    '🎨', '🎭', '🎪', '🎬', '🎤', '🎧', '🎵', '🎸', '🏆', '🥇',
    '🥈', '🥉', '🎖️', '🏅', '🎗️', '🎫', '🎟️', '🎠', '🎡', '🎢',
    '🚀', '✈️', '🚁', '⛵', '🚤', '⛽', '🚦', '🚥', '🚧', '⚡',
    '🔥', '💧', '🌟', '✨', '🌈', '☀️', '🌙', '⭐', '🌍', '🌎'
  ];

  // Auto-save timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load FAQ settings
  useEffect(() => {
    if (!companyId || !configId) return;

    const loadSettings = async () => {
      try {
        setLoading(true);
        console.log('🔄 [FAQ] SQL den yükleniyor:', { companyId, configId });
        
        const response = await getFAQSettings(companyId, configId); // ✅ companyId eklendi
        
        if (response.success && response.data) {
          setSettings({
            enabled: response.data.enabled,
            categories: response.data.categories || [],
            faqs: response.data.faqs || []
          });
          console.log('✅ [FAQ] Ayarlar başarıyla yüklendi:', response.data);
        } else {
          // Veri yoksa default kullan
          setSettings(defaultFAQSettings);
          console.log('ℹ️ [FAQ] Veri bulunamadı, default ayarlar kullanılıyor');
        }
      } catch (err) {
        console.error('❌ [FAQ] Ayarlar yüklenirken hata:', err);
        setError(t('ecommerceFaqSettings.errors.loadFaqSettingsError'));
        // Hata durumunda da default ayarları kullan
        setSettings(defaultFAQSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [companyId, configId]);

  // Debounced auto-save
  const debouncedAutoSave = (newSettings: FAQSettings) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(async () => {
      await autoSave(newSettings);
    }, 1000); // 1 saniye bekle
  };

  // Auto-save function
  const autoSave = async (settingsToSave: FAQSettings) => {
    if (!companyId || !configId) return;

    try {
      console.log('💾 [FAQ AUTO-SAVE] Otomatik kaydediliyor...');
      
      // Ana ayarları kaydet
      await saveFAQSettings({
        configId,
        companyId,
        enabled: settingsToSave.enabled
      });
      
      console.log('✅ [FAQ AUTO-SAVE] Otomatik kayıt başarılı');
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (err) {
      console.error('❌ [FAQ AUTO-SAVE] Otomatik kayıt hatası:', err);
    }
  };

  // Save settings (Manuel kayıt - artık kullanılmayacak)
  const saveSettings = async () => {
    if (!companyId || !configId) return;

    try {
      setSaving(true);
      console.log('💾 [FAQ] SQL e kaydediliyor:', settings);
      
      await saveFAQSettings({
        configId,
        companyId,
        enabled: settings.enabled
      });
      
      console.log('✅ [FAQ] Ayarlar kaydedildi');
      setError(null);
    } catch (err) {
      console.error('❌ [FAQ] Ayarlar kaydedilirken hata:', err);
      setError(t('ecommerceFaqSettings.errors.saveFaqSettingsError'));
    } finally {
      setSaving(false);
    }
  };

  // Category functions
  const openCategoryModal = (category?: FAQCategory) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        icon: category.icon,
        description: category.description
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        icon: '',
        description: ''
      });
    }
    setShowEmojiPicker(false); // Emoji picker'ı kapat
    setCategoryModalError(''); // Hata mesajını temizle
    setCategoryModalOpen(true);
  };

  const saveCategoryModal = async () => {
    // Validasyon
    if (!categoryForm.name.trim()) {
      setCategoryModalError(t('ecommerceFaqSettings.errors.categoryNameRequired'));
      return;
    }
    
    if (!categoryForm.icon.trim()) {
      setCategoryModalError(t('ecommerceFaqSettings.errors.emojiRequired'));
      return;
    }
    
    if (!companyId || !configId) {
      setCategoryModalError(t('ecommerceFaqSettings.errors.companyOrConfigNotFound'));
      return;
    }
    
    setCategoryModalError(''); // Hata mesajını temizle

    try {
      if (editingCategory) {
        // Edit existing category
        const updatedCategory = {
          id: editingCategory.id,
          faqConfigId: faqConfigId || '',
          companyId,
          ...categoryForm,
          enabled: editingCategory.enabled,
          displayOrder: editingCategory.order
        };

        await saveFAQCategory(updatedCategory);

        setSettings(prev => ({
          ...prev,
          categories: prev.categories.map(cat =>
            cat.id === editingCategory.id
              ? { ...cat, ...categoryForm }
              : cat
          )
        }));
        console.log('✅ [FAQ CATEGORY] Kategori güncellendi');
      } else {
        // Add new category
        const newCategory = {
          faqConfigId: faqConfigId || configId,
          companyId,
          ...categoryForm,
          enabled: true,
          displayOrder: settings.categories.length
        };

        const response = await saveFAQCategory(newCategory);

        const addedCategory: FAQCategory = {
          id: response.data.id,
          ...categoryForm,
          enabled: true,
          order: settings.categories.length
        };

        setSettings(prev => ({
          ...prev,
          categories: [...prev.categories, addedCategory]
        }));
        console.log('✅ [FAQ CATEGORY] Yeni kategori eklendi');
      }

      setCategoryModalOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', icon: '', description: '' });
      setShowEmojiPicker(false);
      setCategoryModalError('');
    } catch (err) {
      console.error('❌ [FAQ CATEGORY] Kategori kaydetme hatası:', err);
      setCategoryModalError(t('ecommerceFaqSettings.errors.saveCategoryError'));
    }
  };

  const openDeleteCategoryModal = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setDeleteCategoryModalOpen(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete || !companyId) return; // ✅ companyId null check

    // Kategoriye ait soru sayısını kontrol et
    const categoryFaqCount = settings.faqs.filter(f => f.categoryId === categoryToDelete).length;
    
    if (categoryFaqCount > 0) {
      console.log('⚠️ [FAQ CATEGORY] Kategoride soru var, silinemez:', categoryFaqCount);
      setError(t('ecommerceFaqSettings.errors.categoryHasQuestionsDeleteFirst', { count: categoryFaqCount }));
      setDeleteCategoryModalOpen(false);
      setCategoryToDelete(null);
      return;
    }

    try {
      await deleteFAQCategory(categoryToDelete, companyId); // ✅ companyId eklendi

      setSettings(prev => ({
        ...prev,
        categories: prev.categories.filter(cat => cat.id !== categoryToDelete)
      }));
      console.log('✅ [FAQ CATEGORY] Kategori silindi');
      setDeleteCategoryModalOpen(false);
      setCategoryToDelete(null);
    } catch (err) {
      console.error('❌ [FAQ CATEGORY] Kategori silme hatası:', err);
      setError(t('ecommerceFaqSettings.errors.deleteCategoryError'));
    }
  };

  // FAQ functions
  const openFaqModal = (faq?: FAQ) => {
    if (faq) {
      setEditingFaq(faq);
      setFaqForm({
        question: faq.question,
        answer: faq.answer,
        categoryId: faq.categoryId
      });
    } else {
      setEditingFaq(null);
      setFaqForm({
        question: '',
        answer: '',
        categoryId: settings.categories[0]?.id || ''
      });
    }
    setFaqModalError(''); // Hata mesajını temizle
    setFaqModalOpen(true);
  };

  const saveFaqModal = async () => {
    console.log('🔵 [FAQ ITEM] saveFaqModal çağrıldı');
    console.log('📝 [FAQ ITEM] Form verileri:', faqForm);
    console.log('👤 [FAQ ITEM] CompanyId:', companyId);
    
    // Validasyon
    if (!faqForm.categoryId) {
      console.log('⚠️ [FAQ ITEM] Kategori seçilmemiş!');
      setFaqModalError(t('ecommerceFaqSettings.errors.selectCategory'));
      return;
    }
    
    if (!faqForm.question.trim()) {
      console.log('⚠️ [FAQ ITEM] Soru boş!');
      setFaqModalError(t('ecommerceFaqSettings.errors.questionRequired'));
      return;
    }
    
    if (!faqForm.answer.trim()) {
      console.log('⚠️ [FAQ ITEM] Cevap boş!');
      setFaqModalError(t('ecommerceFaqSettings.errors.answerRequired'));
      return;
    }
    
    if (!companyId) {
      console.log('⚠️ [FAQ ITEM] CompanyId yok!');
      setFaqModalError(t('ecommerceFaqSettings.errors.companyNotFound'));
      return;
    }
    
    setFaqModalError(''); // Hata mesajını temizle

    try {
      if (editingFaq) {
        console.log('✏️ [FAQ ITEM] Mevcut soru güncelleniyor:', editingFaq.id);
        // Edit existing FAQ
        const updatedFaq = {
          id: editingFaq.id,
          categoryId: faqForm.categoryId,
          companyId,
          question: faqForm.question,
          answer: faqForm.answer,
          enabled: editingFaq.enabled,
          displayOrder: editingFaq.order,
          helpfulCount: editingFaq.helpful,
          notHelpfulCount: editingFaq.notHelpful
        };

        await saveFAQItem(updatedFaq);

        setSettings(prev => ({
          ...prev,
          faqs: prev.faqs.map(faq =>
            faq.id === editingFaq.id
              ? { ...faq, ...faqForm }
              : faq
          )
        }));
        console.log('✅ [FAQ ITEM] Soru güncellendi');
      } else {
        console.log('➕ [FAQ ITEM] Yeni soru ekleniyor');
        // Add new FAQ
        const newFaq = {
          categoryId: faqForm.categoryId,
          companyId,
          question: faqForm.question,
          answer: faqForm.answer,
          enabled: true,
          displayOrder: settings.faqs.filter(f => f.categoryId === faqForm.categoryId).length,
          helpfulCount: 0,
          notHelpfulCount: 0
        };

        console.log('📤 [FAQ ITEM] API ye gönderiliyor:', newFaq);
        const response = await saveFAQItem(newFaq);

        const addedFaq: FAQ = {
          id: response.data.id,
          ...faqForm,
          enabled: true,
          helpful: 0,
          notHelpful: 0,
          order: settings.faqs.filter(f => f.categoryId === faqForm.categoryId).length
        };

        setSettings(prev => ({
          ...prev,
          faqs: [...prev.faqs, addedFaq]
        }));
        console.log('✅ [FAQ ITEM] Yeni soru eklendi');
      }

      setFaqModalOpen(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', categoryId: '' });
      setFaqModalError('');
    } catch (err) {
      console.error('❌ [FAQ ITEM] Soru kaydetme hatası:', err);
      setFaqModalError(t('ecommerceFaqSettings.errors.saveQuestionError'));
    }
  };

  const openDeleteFaqModal = (faqId: string) => {
    setFaqToDelete(faqId);
    setDeleteFaqModalOpen(true);
  };

  const confirmDeleteFaq = async () => {
    if (!faqToDelete || !companyId) return; // ✅ companyId null check

    try {
      await deleteFAQItem(faqToDelete, companyId); // ✅ companyId eklendi

      setSettings(prev => ({
        ...prev,
        faqs: prev.faqs.filter(faq => faq.id !== faqToDelete)
      }));
      console.log('✅ [FAQ ITEM] Soru silindi');
      setDeleteFaqModalOpen(false);
      setFaqToDelete(null);
    } catch (err) {
      console.error('❌ [FAQ ITEM] Soru silme hatası:', err);
      setError(t('ecommerceFaqSettings.errors.deleteQuestionError'));
    }
  };

  const toggleCategoryEnabled = async (categoryId: string) => {
    const category = settings.categories.find(cat => cat.id === categoryId);
    if (!category || !companyId) return;

    try {
      const updatedCategory = {
        id: category.id,
        faqConfigId: faqConfigId || configId,
        companyId,
        name: category.name,
        icon: category.icon,
        description: category.description,
        enabled: !category.enabled,
        displayOrder: category.order
      };

      await saveFAQCategory(updatedCategory);

      setSettings(prev => ({
        ...prev,
        categories: prev.categories.map(cat =>
          cat.id === categoryId
            ? { ...cat, enabled: !cat.enabled }
            : cat
        )
      }));
      console.log('✅ [FAQ CATEGORY] Kategori durumu güncellendi');
    } catch (err) {
      console.error('❌ [FAQ CATEGORY] Kategori durumu güncelleme hatası:', err);
      setError(t('ecommerceFaqSettings.errors.updateCategoryStatusError'));
    }
  };

  const toggleFaqEnabled = async (faqId: string) => {
    const faq = settings.faqs.find(f => f.id === faqId);
    if (!faq || !companyId) return;

    try {
      const updatedFaq = {
        id: faq.id,
        categoryId: faq.categoryId,
        companyId,
        question: faq.question,
        answer: faq.answer,
        enabled: !faq.enabled,
        displayOrder: faq.order,
        helpfulCount: faq.helpful,
        notHelpfulCount: faq.notHelpful
      };

      await saveFAQItem(updatedFaq);

      setSettings(prev => ({
        ...prev,
        faqs: prev.faqs.map(faq =>
          faq.id === faqId
            ? { ...faq, enabled: !faq.enabled }
            : faq
        )
      }));
      console.log('✅ [FAQ ITEM] Soru durumu güncellendi');
    } catch (err) {
      console.error('❌ [FAQ ITEM] Soru durumu güncelleme hatası:', err);
      setError(t('ecommerceFaqSettings.messages.updateQuestionStatusError'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>{t('ecommerceFaqSettings.common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HelpOutlineIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            {t('ecommerceFaqSettings.title')}
          </Typography>
        </Box>
        {saving && (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            ✨ {t('ecommerceFaqSettings.common.autoSaving')}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={mainTab}
        onChange={(_e, value: number) => setMainTab(value)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<ViewCarouselOutlinedIcon />}
          iconPosition="start"
          label="Sayfa SSS Blokları"
          sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
        />
        <Tab
          icon={<PersonOutlineIcon />}
          iconPosition="start"
          label="Hesabım SSS"
          sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
        />
      </Tabs>

      {mainTab === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            İçerik kartları gibi yönetilir: şablon ve hedef sayfa seçin; blok footer&apos;ın hemen üstüne
            yerleşir (SSS → Bülten → Footer).
          </Typography>
          {companyId && configId ? (
            <FaqSectionsPanel companyId={companyId} configId={configId} />
          ) : (
            <Alert severity="warning">
              Sayfa SSS bloklarını kullanmak için önce e-ticaret yapılandırmasını kaydedin.
            </Alert>
          )}
        </Box>
      )}

      {mainTab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bu sekme yalnızca <strong>Hesabım → SSS</strong> (<code>/account/faq</code>) sayfasını yönetir.
          </Alert>

      {/* Genel Ayarlar Accordion */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceFaqSettings.sections.generalSettings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => {
                    const newSettings = { ...settings, enabled: e.target.checked };
                    setSettings(newSettings);
                    debouncedAutoSave(newSettings);
                  }}
                />
              }
              label={t('ecommerceFaqSettings.general.faqSystemActive')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('ecommerceFaqSettings.general.faqSystemActiveHelp')}
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Categories Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceFaqSettings.categories.titleWithCount', { count: settings.categories.length })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('ecommerceFaqSettings.categories.description')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => openCategoryModal()}
            >
              {t('ecommerceFaqSettings.categories.addCategory')}
            </Button>
          </Box>

          <Grid container spacing={2}>
            {settings.categories.map((category) => (
              <Grid item xs={12} md={6} key={category.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ mr: 1 }}>
                        {category.icon}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {category.name}
                      </Typography>
                      <Chip
                        label={category.enabled ? t('ecommerceFaqSettings.common.active') : t('ecommerceFaqSettings.common.passive')}
                        color={category.enabled ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {category.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('ecommerceFaqSettings.categories.questionsCount', { count: settings.faqs.filter(f => f.categoryId === category.id).length })}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => toggleCategoryEnabled(category.id)}
                    >
                      {category.enabled ? t('ecommerceFaqSettings.actions.setPassive') : t('ecommerceFaqSettings.actions.setActive')}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openCategoryModal(category)}
                    >
                      {t('ecommerceFaqSettings.actions.edit')}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => openDeleteCategoryModal(category.id)}
                    >
                      {t('ecommerceFaqSettings.actions.delete')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* FAQs Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <QuestionAnswerIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceFaqSettings.faqs.titleWithCount', { count: settings.faqs.length })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('ecommerceFaqSettings.faqs.description')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => openFaqModal()}
            >
              {t('ecommerceFaqSettings.faqs.addQuestion')}
            </Button>
          </Box>

          {settings.categories.map((category) => {
            const categoryFaqs = settings.faqs.filter(faq => faq.categoryId === category.id);
            if (categoryFaqs.length === 0) return null;

            return (
              <Accordion key={category.id} defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {category.icon} {category.name} ({categoryFaqs.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {categoryFaqs.map((faq) => (
                      <Grid item xs={12} key={faq.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                                {faq.question}
                              </Typography>
                              <Chip
                                label={faq.enabled ? t('ecommerceFaqSettings.common.active') : t('ecommerceFaqSettings.common.passive')}
                                color={faq.enabled ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {faq.answer.length > 100 ? `${faq.answer.substring(0, 100)}...` : faq.answer}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Typography variant="caption" color="success.main">
                                👍 {faq.helpful}
                              </Typography>
                              <Typography variant="caption" color="error.main">
                                👎 {faq.notHelpful}
                              </Typography>
                            </Box>
                          </CardContent>
                          <CardActions>
                            <Button
                              size="small"
                              onClick={() => toggleFaqEnabled(faq.id)}
                            >
                              {faq.enabled ? t('ecommerceFaqSettings.actions.setPassive') : t('ecommerceFaqSettings.actions.setActive')}
                            </Button>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => openFaqModal(faq)}
                            >
                              {t('ecommerceFaqSettings.actions.edit')}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => openDeleteFaqModal(faq.id)}
                            >
                              {t('ecommerceFaqSettings.actions.delete')}
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {settings.faqs.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('ecommerceFaqSettings.faqs.empty')}
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
        </Box>
      )}

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? t('ecommerceFaqSettings.modals.editCategory') : t('ecommerceFaqSettings.modals.newCategory')}
        </DialogTitle>
        <DialogContent>
          {categoryModalError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {categoryModalError}
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label={t('ecommerceFaqSettings.fields.categoryName')}
              value={categoryForm.name}
              onChange={(e) => {
                setCategoryForm(prev => ({ ...prev, name: e.target.value }));
                if (categoryModalError) setCategoryModalError('');
              }}
              sx={{ mb: 2 }}
              required
              error={Boolean(categoryModalError)}
            />
            
            {/* Emoji Seçici */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('ecommerceFaqSettings.fields.iconEmoji')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  value={categoryForm.icon}
                  onChange={(e) => {
                    setCategoryForm(prev => ({ ...prev, icon: e.target.value }));
                    if (categoryModalError) setCategoryModalError('');
                  }}
                  placeholder={t('ecommerceFaqSettings.fields.iconPlaceholder')}
                  sx={{ width: '120px' }}
                  inputProps={{ style: { fontSize: '24px', textAlign: 'center' } }}
                  required
                  error={Boolean(categoryModalError)}
                />
                <Button
                  variant="outlined"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  sx={{ minWidth: '120px' }}
                >
                  {showEmojiPicker ? t('ecommerceFaqSettings.actions.hide') : t('ecommerceFaqSettings.actions.selectEmoji')}
                </Button>
              </Box>
              
              {showEmojiPicker && (
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Grid container spacing={1}>
                    {commonEmojis.map((emoji, index) => (
                      <Grid item key={index}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setCategoryForm(prev => ({ ...prev, icon: emoji }));
                            setShowEmojiPicker(false);
                          }}
                          sx={{ 
                            minWidth: '40px', 
                            height: '40px', 
                            fontSize: '20px',
                            p: 0
                          }}
                        >
                          {emoji}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}
            </Box>
            <TextField
              fullWidth
              label={t('ecommerceAboutSettings.fields.description')}
              value={categoryForm.description}
              onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryModalOpen(false)}>{t('ecommerceAboutSettings.actions.cancel')}</Button>
          <Button onClick={saveCategoryModal} variant="contained">
            {editingCategory ? t('ecommerceFaqSettings.actions.update') : t('ecommerceFaqSettings.actions.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ Modal */}
      <Dialog open={faqModalOpen} onClose={() => setFaqModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFaq ? t('ecommerceFaqSettings.modals.editQuestion') : t('ecommerceFaqSettings.modals.newQuestion')}
        </DialogTitle>
        <DialogContent>
          {faqModalError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {faqModalError}
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <FormControl 
              fullWidth 
              sx={{ mb: 2 }} 
              required
              error={Boolean(faqModalError)}
            >
              <InputLabel>{t('ecommerceFaqSettings.fields.categoryRequired')}</InputLabel>
              <Select
                value={faqForm.categoryId}
                onChange={(e) => {
                  setFaqForm(prev => ({ ...prev, categoryId: e.target.value }));
                  if (faqModalError) setFaqModalError('');
                }}
                label={t('ecommerceFaqSettings.fields.categoryRequired')}
              >
                {settings.categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={t('ecommerceFaqSettings.fields.question')}
              value={faqForm.question}
              onChange={(e) => {
                setFaqForm(prev => ({ ...prev, question: e.target.value }));
                if (faqModalError) setFaqModalError('');
              }}
              sx={{ mb: 2 }}
              required
              error={Boolean(faqModalError)}
            />
            <TextField
              fullWidth
              label={t('ecommerceFaqSettings.fields.answer')}
              value={faqForm.answer}
              onChange={(e) => {
                setFaqForm(prev => ({ ...prev, answer: e.target.value }));
                if (faqModalError) setFaqModalError('');
              }}
              multiline
              rows={4}
              required
              error={Boolean(faqModalError)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqModalOpen(false)}>{t('ecommerceAboutSettings.actions.cancel')}</Button>
          <Button 
            onClick={() => {
              console.log('🔘 [FAQ ITEM] Add button clicked');
              saveFaqModal();
            }} 
            variant="contained"
          >
            {editingFaq ? t('ecommerceFaqSettings.actions.update') : t('ecommerceFaqSettings.actions.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Category Confirmation Modal */}
      <Dialog 
        open={deleteCategoryModalOpen} 
        onClose={() => setDeleteCategoryModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {t('ecommerceFaqSettings.modals.categoryDeletionConfirmation')}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const categoryFaqCount = categoryToDelete 
              ? settings.faqs.filter(f => f.categoryId === categoryToDelete).length 
              : 0;
            
            if (categoryFaqCount > 0) {
              return (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {t('ecommerceFaqSettings.modals.categoryCannotBeDeleted')}
                    </Typography>
                  </Alert>
                  <Typography>
                    {t('ecommerceFaqSettings.modals.categoryHasQuestions', { count: categoryFaqCount })}
                  </Typography>
                  <Typography sx={{ mt: 2 }}>
                    {t('ecommerceFaqSettings.modals.deleteLinkedQuestionsFirst')}
                  </Typography>
                </>
              );
            }
            
            return (
              <>
                <Typography>
                  {t('ecommerceFaqSettings.modals.confirmDeleteCategory')}
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t('ecommerceFaqSettings.modals.cannotBeUndone')}
                </Alert>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteCategoryModalOpen(false);
            setCategoryToDelete(null);
          }}>
            {categoryToDelete && settings.faqs.filter(f => f.categoryId === categoryToDelete).length > 0 
              ? t('ecommerceFaqSettings.actions.ok') 
              : t('ecommerceAboutSettings.actions.cancel')}
          </Button>
          {categoryToDelete && settings.faqs.filter(f => f.categoryId === categoryToDelete).length === 0 && (
            <Button 
              onClick={confirmDeleteCategory} 
              color="error" 
              variant="contained"
              startIcon={<DeleteIcon />}
            >
              {t('ecommerceFaqSettings.actions.yesDelete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete FAQ Confirmation Modal */}
      <Dialog 
        open={deleteFaqModalOpen} 
        onClose={() => setDeleteFaqModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {t('ecommerceFaqSettings.modals.questionDeletionConfirmation')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('ecommerceFaqSettings.modals.confirmDeleteQuestion')}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('ecommerceFaqSettings.modals.cannotBeUndone')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFaqModalOpen(false)}>
            {t('ecommerceAboutSettings.actions.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteFaq} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            {t('ecommerceFaqSettings.actions.yesDelete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FAQSettingsComponent;
